import { useState, useEffect, useRef } from 'react';
import { useConsent } from '../contexts/ConsentContext';
import { ConsentPreferences, CONSENT_CATEGORIES } from '../lib/consent';
import { X, Check, Shield, Activity, Sliders, Tag, ChevronDown, ChevronUp } from 'lucide-react';
import FocusTrapModal from './FocusTrapModal';

export default function CookieSettingsModal() {
  const { showSettings, preferences, closeSettings, updatePreferences, isGPCEnabled } = useConsent();
  const [localPreferences, setLocalPreferences] = useState<ConsentPreferences>(preferences);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    console.log('[CookieSettingsModal.useEffect] showSettings:', showSettings, 'preferences from context:', preferences);
    if (showSettings) {
      setLocalPreferences(preferences);
    }
  }, [showSettings, preferences]);

  const handleToggle = (category: keyof ConsentPreferences) => {
    if (category === 'essential') return;
    console.log('[CookieSettingsModal.handleToggle] Toggling:', category);
    setLocalPreferences(prev => {
      const updated = {
        ...prev,
        [category]: !prev[category],
      };
      console.log('[CookieSettingsModal.handleToggle] New localPreferences:', updated);
      return updated;
    });
  };

  const handleSave = () => {
    console.log('[CookieSettingsModal.handleSave] Saving localPreferences:', localPreferences);
    updatePreferences(localPreferences);
  };

  const handleAcceptAll = () => {
    console.log('[CookieSettingsModal.handleAcceptAll] Called');
    const allAccepted: ConsentPreferences = {
      essential: true,
      analytics: true,
      functionality: true,
      advertising: true,
    };
    console.log('[CookieSettingsModal.handleAcceptAll] Setting all to:', allAccepted);
    setLocalPreferences(allAccepted);
    updatePreferences(allAccepted);
  };

  const toggleExpanded = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const getCategoryIcon = (categoryId: string) => {
    switch (categoryId) {
      case 'essential':
        return <Shield className="w-5 h-5" />;
      case 'analytics':
        return <Activity className="w-5 h-5" />;
      case 'functionality':
        return <Sliders className="w-5 h-5" />;
      case 'advertising':
        return <Tag className="w-5 h-5" />;
      default:
        return null;
    }
  };

  return (
    <FocusTrapModal
      isOpen={showSettings}
      onClose={closeSettings}
      ariaLabelledBy="cookie-settings-title"
      ariaDescribedBy="cookie-settings-description"
      initialFocusRef={closeButtonRef}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 id="cookie-settings-title" className="text-2xl font-heading font-bold text-gray-900">
              Cookie Settings
            </h2>
            <p id="cookie-settings-description" className="text-sm text-gray-800 mt-1">
              Manage your cookie preferences
            </p>
          </div>
          <button
            ref={closeButtonRef}
            onClick={closeSettings}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close cookie settings"
          >
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>

          {isGPCEnabled && (
            <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Global Privacy Control detected:</strong> Non-essential cookies have been disabled automatically.
                You can still adjust your preferences below.
              </p>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-6">
            <p className="text-sm text-gray-800 mb-6 leading-relaxed">
              We use different types of cookies to optimize your experience on our website. Click on the categories
              below to learn more and adjust your preferences. Note that blocking some types of cookies may impact
              your experience on our site.
            </p>

            <div className="space-y-4">
              {Object.values(CONSENT_CATEGORIES).map((category) => {
                const isEnabled = localPreferences[category.id];
                const isExpanded = expandedCategories.has(category.id);

                return (
                  <div
                    key={category.id}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div className="p-4 bg-gray-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`mt-1 ${isEnabled ? 'text-primary' : 'text-gray-400'}`}>
                            {getCategoryIcon(category.id)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-heading font-bold text-gray-900">
                                {category.name}
                              </h3>
                              {category.required && (
                                <span className="text-xs bg-gray-200 text-gray-800 px-2 py-0.5 rounded-full font-medium">
                                  Always Active
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-800 leading-relaxed">
                              {category.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleExpanded(category.id)}
                            className="p-2 hover:bg-gray-200 rounded transition-colors"
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-800" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-800" />
                            )}
                          </button>

                          <button
                            onClick={() => handleToggle(category.id)}
                            disabled={category.required}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                              isEnabled ? 'bg-primary' : 'bg-gray-300'
                            } ${category.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            aria-label={`Toggle ${category.name}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                isEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="p-4 bg-white border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">
                          Cookies used in this category:
                        </h4>
                        <div className="space-y-3">
                          {category.cookies.map((cookie, index) => (
                            <div key={index} className="text-sm">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded text-gray-800">
                                  {cookie.name}
                                </span>
                                <span className="text-xs text-gray-700 whitespace-nowrap">
                                  {cookie.duration}
                                </span>
                              </div>
                              <p className="text-gray-800">{cookie.purpose}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={closeSettings}
              className="px-6 py-3 border-2 border-gray-300 text-gray-900 rounded-lg hover:bg-white transition-colors font-medium"
            >
              Cancel
            </button>
            <div className="flex gap-3">
              <button
                onClick={handleAcceptAll}
                className="px-6 py-3 border-2 border-primary text-primary rounded-lg hover:bg-primary hover:text-white transition-colors font-medium"
              >
                Accept All
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium shadow-md"
              >
                <Check className="w-4 h-4" />
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      </FocusTrapModal>
  );
}
